import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { debounceTime } from 'rxjs/operators';
import { GroupsApi, PagedContactResponse, PagedGroups } from 'src/app/hickory-api';
import { ContactSelection, GroupSelection, PagedList } from '../model';

export interface SelectGroupsData {
  selectedGroups: GroupSelection[],
  selectedContacts: ContactSelection[],
  mandatoryGroups?: GroupSelection[],
  contactsReadonly?: boolean,
  classification?: 'Internal' | 'Public',
}
@Component({
  selector: 'app-select-groups',
  templateUrl: './select-groups.component.html',
  styleUrls: ['./select-groups.component.scss'],
  standalone: false
})
export class SelectGroupsComponent implements OnInit {
  groupStack: GroupSelection[] = GroupSelection.topStackItem();
  contacts: PagedList<ContactSelection>;
  loadGroupsFailed: boolean = false;
  loadGroupContactsFailed: boolean = false;
  groupSearchControl = new UntypedFormControl();
  private groupSearch: string;
  private readonly pageSize = 10;

  @Output() error: EventEmitter<any> = new EventEmitter();

  get selectedContacts() {
    return this.data?.selectedContacts || [];
  }

  get selectedGroups() {
    return this.data?.selectedGroups || [];
  }

  get group(): GroupSelection {
    return this.groupStack[this.groupStack.length - 1];
  }

  set group(group: GroupSelection) {
    this.groupStack.push(group); // set group as parent and current group
    this.loadGroupsPage(); // Already loaded page 1, just need to pre-populate children
  }

  get completed(): boolean {
    return this.selectedGroups.length > 0 || this.selectedContacts.length > 0;
  }

  constructor(
    private dialogRef: MatDialogRef<SelectGroupsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SelectGroupsData,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private groupApi: GroupsApi
  ) {
    if (!this.data) {
      this.data = { selectedContacts: [], selectedGroups: [] };
    }
  }

  ngOnInit() {

    // Debounce group search and trigger the refresh
    this.groupSearchControl.valueChanges.pipe(debounceTime(500)).subscribe(newValue => {
      this.groupSearch = newValue;
      this.loadGroupsPage();
    });

    this.loadGroupsPage();
  }

  loadGroupsPage(page: number = 1) {
    if (this.group.id) {
      if (!this.group.contacts) {
        this.loadGroupContacts(this.group);
      }
      // If we're paging subgroups (page 1 already loaded initially)
      if (this.group.subgroups && this.group.subgroups.page !== page) {
        this.group.subgroups = null;
        this.loadSubGroupsFor(this.group, page).then(r => {
          // Load children of this group
          if (this.group.subgroups) {
            this.group.subgroups.items.forEach(group => {
              this.loadSubGroupsFor(group);
              this.loadGroupContacts(group);
            });
          }
        });
      } else if (this.group.subgroups) {
        // Build drilldown link for any group with subgroups or contacts
        this.group.subgroups.items.forEach(group => {
          this.loadSubGroupsFor(group);
          this.loadGroupContacts(group);
        });
      }
    } else {
      this.loadGroupsFailed = false;
      this.group.subgroups = null;
      this.groupApi.getGroupsV21(null, null, page, this.pageSize, this.groupSearch, this.data.classification).subscribe({
        next: r => {
          this.group.subgroups = GroupSelection.convertGroups(this.selectedGroups, r as PagedGroups, this.group.selected);
          // Load children of this group
          this.group.subgroups.items.forEach(group => {
            this.loadSubGroupsFor(group);
            this.loadGroupContacts(group);
          });
        }, error: e => {
          this.loadGroupsFailed = true;
          this.error.emit(e)
        }
      });
    }
  }

  loadSubGroupsFor(group: GroupSelection, page = 1): Promise<PagedList<GroupSelection>> {
    return new Promise((resolve, reject) => {
      this.groupApi.getSubgroupsByParentId(group.id, page, this.pageSize).subscribe({
        next: r => {
          group.subgroups = GroupSelection.convertGroups(this.selectedGroups, r as PagedGroups, group.selected);
          resolve(group.subgroups);
        }, error: e => {
          reject(e);
          this.error.emit(e);
        }
      });
    });
  }

  loadGroupContacts(group: GroupSelection, page = 1) {
    this.loadGroupContactsFailed = false;
    group.contacts = null;
    this.groupApi.getContactsByGroupId(group.id, null, page, this.pageSize).subscribe({
      next: r => {
        group.contacts = ContactSelection.convertContacts(this.selectedContacts, r as PagedContactResponse, group.selected, this.data?.contactsReadonly);
      }, error: e => {
        this.loadGroupContactsFailed = true;
        this.error.emit(e)
      }
    });
  }

  onBack() {
    // Go back to parent group
    this.groupStack.pop();
  }

  onContactSelected(contact: ContactSelection, selected: boolean) {
    if (this.data?.contactsReadonly) return;
    const alreadySelected = this.selectedContacts.find(c => c.identifierEquals(contact));
    if (!alreadySelected) {

      // mark it as selected for groups
      contact.selected = true;
      this.selectedContacts.push(contact);
    } else if (alreadySelected) {
      this.selectedContacts.splice(this.selectedContacts.indexOf(alreadySelected), 1);
      contact.selected = false;
    }
  }

  onGroupContactsPageChange(page: number) {
    this.loadGroupContacts(this.group, page);
  }

  onGroupSelected(group: GroupSelection, selected: boolean) {
    group.selected = selected;
    if (selected) {
      this.changeSubGroups(group, selected, selected);
      this.selectedGroups.push(group);
    } else if (this.selectedGroups.indexOf(group) > -1) {
      this.changeSubGroups(group, selected, selected);
      this.selectedGroups.splice(this.selectedGroups.indexOf(group), 1);
    }
  }

  onGroupPageChange(page: number) {
    this.loadGroupsPage(page);
  }

  changeSubGroups(group: GroupSelection, selected: boolean, readonly: boolean) {
    // selecting or deselecting a group should select subgroups and contacts and put them in read only 
    // mode to visually show them selected but you can't manipulate them if the parent is selected.
    if (group.subgroups && group.subgroups.items) {
      group.subgroups.items.forEach(g => {
        // if group isn't a mag, change it
        const mandatory = this.data.mandatoryGroups?.find(m => m.identifierEquals(g));
        if (!mandatory) {
          g.selected = selected;
          g.readonly = readonly;
        }
      });
    }
    if (group.contacts && group.contacts.items) {
      group.contacts.items.forEach(c => {
        c.selected = selected;
        c.readonly = readonly;
      });
    }
  }

  onGroupDrilldown(group: GroupSelection) {
    this.group = group;
  }

  onBackClick() {
    this.groupStack.pop();
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onSaveClick() {
    const data: SelectGroupsData = {
      selectedGroups: this.selectedGroups,
      selectedContacts: this.selectedContacts,
    }
    this.dialogRef.close(data);
  }

}
